import type { StackProps } from '@hover/blueprint';
import {
  Body,
  Box,
  HStack,
  VStack,
  forwardRef,
  motion,
  transition,
  useImage,
} from '@hover/blueprint';
import type { MouseEventHandler } from 'react';
import React from 'react';

import {
  imageVariants,
  useFormattedTimeAgo,
  variants,
} from 'src/modules/homeownerApp/hooks';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { altStyle, useImageFocus } from '../../helpers/styles';
import { FullscreenImageThumbnail } from '../FullscreenImage';
import { ThumbnailEngagementButtons } from '../ThumbnailEngagementButtons';
import { formatSecondaryTextForDisplayImage, formatSpaceType } from '../Utils';
import { ImageError } from '../errors';

export interface ImageCardProps extends StackProps {
  createdAt?: string;
  primaryImageUrl?: string;
  thumbnailImageUrl?: string;
  displayImage?: AiDisplayImage;
  orientation?: string | null;
  styleName?: string;
  spaceType?: string | null;
  subText?: string;
}

const imageAspectRatio = (orientation?: string | null): number => {
  const verticalRatio: number = 3 / 4;
  const horizontalRatio: number = 4 / 3;
  if (!orientation) return verticalRatio;
  return orientation === 'vertical' ? verticalRatio : horizontalRatio;
};

const ImageCard = forwardRef<ImageCardProps, 'figure'>(
  (
    {
      createdAt,
      displayImage,
      orientation,
      styleName,
      spaceType,
      onClick,
      primaryImageUrl,
      subText,
      thumbnailImageUrl,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const dateCreated = useFormattedTimeAgo(createdAt);

    const onImageClick: MouseEventHandler<
      HTMLButtonElement & HTMLDivElement
    > = event => {
      onClick?.(event);
    };

    const primaryImageError = !!thumbnailImageUrl && !primaryImageUrl;
    const aiGeneratedImage = displayImage?.aiGeneratedImage;

    // If there is no primary image url, use the provided thumbnail as the error state image
    // otherwise use the generated image path
    const primaryDisplayImageUrl =
      primaryImageUrl ||
      thumbnailImageUrl ||
      aiGeneratedImage?.generatedImagePath;

    const imageProps = { src: primaryDisplayImageUrl } as const;
    const thumbProps = { src: thumbnailImageUrl, loading: 'lazy' } as const;
    const imageStatus = useImage(imageProps);
    const thumbStatus = useImage(thumbProps);

    const secondaryText = formatSecondaryTextForDisplayImage(
      t('generatedImageThumbnail.customized'),
      dateCreated,
      aiGeneratedImage,
    );
    const error = imageStatus === 'failed';

    const focus = useImageFocus('img.primary-image');

    const originalLabel = t('imageCard.originalAlt', { styleName, spaceType });
    const generatedLabel = t('imageCard.generatedAlt', {
      styleName,
      spaceType,
    });
    const label = aiGeneratedImage ? generatedLabel : originalLabel;

    const formattedSpaceType = spaceType && formatSpaceType(spaceType);
    const text = formattedSpaceType
      ? `${formattedSpaceType} ${secondaryText}`
      : label;

    return (
      <VStack
        alignItems="start"
        aria-label={text}
        className="masonry-content"
        data-group
        role="listitem"
        spacing="200"
        {...props}
        ref={ref}
      >
        <FullscreenImageThumbnail
          as="figure"
          bg="darkAlpha.50"
          height="100%"
          image={displayImage}
          m={0}
          overflow="hidden"
          position="relative"
          rounded="200"
          width="100%"
        >
          <Box
            _hover={{ filter: 'brightness(0.8)' }}
            aria-label={label}
            as="button"
            border="none"
            borderRadius="inherit"
            cursor="pointer"
            filter="brightness(1)"
            height="100%"
            onClick={onImageClick}
            p="0"
            position="relative"
            sx={focus}
            width="100%"
            {...transition.easing250('filter')}
          >
            {error ? (
              <ImageError
                bg="darkAlpha.50"
                color="darkAlpha.400"
                message={t('imageCard.failedToLoad')}
                sx={{ aspectRatio: '4/3' }}
                width="100%"
              />
            ) : (
              <motion.img
                alt={originalLabel}
                animate={imageVariants.loaded}
                className="primary-image"
                cursor="pointer"
                height="100%"
                initial={imageVariants.loaded}
                objectFit="cover"
                sx={{
                  aspectRatio: orientation
                    ? String(imageAspectRatio(orientation))
                    : undefined,
                }}
                variants={variants}
                width="100%"
                {...imageProps}
                {...altStyle()}
              />
            )}
            {primaryImageError && (
              <ImageError
                color="neutral.0"
                height="100%"
                position="absolute"
                width="100%"
              />
            )}
            {thumbnailImageUrl && thumbStatus !== 'failed' && (
              <motion.img
                alt={generatedLabel}
                borderColor="neutral.0"
                borderRadius={200}
                borderStyle="solid"
                borderWidth={400}
                height="30%"
                left={{ base: 300, tablet: 600 }}
                objectFit="cover"
                pointerEvents="none"
                position="absolute"
                top={{ base: 300, tablet: 600 }}
                width="30%"
                {...thumbProps}
                {...altStyle('body300')}
              />
            )}
          </Box>
          {aiGeneratedImage && (
            <ThumbnailEngagementButtons image={displayImage} />
          )}
        </FullscreenImageThumbnail>

        <HStack
          alignItems="start"
          justifyContent="space-between"
          paddingBottom={{ base: 200, tablet: 0 }}
          paddingTop={100}
          spacing={100}
        >
          <VStack alignItems="start" spacing="100">
            {spaceType && (
              <Body isSemiBold m={0}>
                {subText || formattedSpaceType}
              </Body>
            )}
            {secondaryText && (
              <Body color="neutral.500" m="0 !important">
                {secondaryText}
              </Body>
            )}
          </VStack>
        </HStack>
      </VStack>
    );
  },
);

export { ImageCard, imageAspectRatio };
